/**
 * Module configuration for all admin pages that previously displayed the
 * generic ComingSoonPage placeholder. Each entry produces a fully functional
 * CRUD page via GenericModulePage — editors get real storage instead of a
 * "module in development" message.
 *
 * Keep icons consistent with the sidebar (AdminLayout.js) so navigation and
 * page-level icons match.
 */
import {
  Activity as LiveIcon, Zap, AlertTriangle, Radio, UsersRound, Users as UsersIcon,
  ListOrdered, MessageSquare, Book, FileText as FileTextIcon, LayoutList, Megaphone,
  Map, Bitcoin, Eye, TrendingUp, History, UserCheck, Link2, Database, FileCheck,
  BarChart, LineChart, CalendarClock, Store, Briefcase,
} from 'lucide-react';

export const MODULE_CONFIGS = {
  // ── Manage ──────────────────────────────────────────────────────────────
  'analytics-live': {
    title: 'Analitika uživo',
    description: 'Dodatni praćeni segmenti za real-time dashboard (uvijek aktivni paneli na početnoj).',
    icon: LiveIcon,
    newLabel: 'Novi segment',
    emptyHint: 'Još nema praćenih segmenata. Dodajte primjer: "Prometna nesreća" ili "Vikend trendovi".',
    fields: [
      { key: 'metric', label: 'Metrika', type: 'select',
        options: [
          { value: 'pageviews', label: 'Pregledi' },
          { value: 'visitors', label: 'Jedinstveni posjetitelji' },
          { value: 'sessions', label: 'Sesije' },
          { value: 'ctr', label: 'CTR' },
        ] },
      { key: 'threshold', label: 'Prag upozorenja', type: 'number', placeholder: '100' },
    ],
  },
  'breaking-news': {
    title: 'Vijesti u prekidu',
    description: 'Ručno upisane "breaking" najave koje se prikazuju u crvenoj vrpci ispod headera.',
    icon: Zap,
    newLabel: 'Nova breaking vijest',
    fields: [
      { key: 'url', label: 'Link (izborno)', type: 'url', placeholder: 'https://e-mina.eu/clanak/...' },
      { key: 'color', label: 'Boja vrpce', type: 'select',
        options: [
          { value: 'red', label: 'Crvena (default)' },
          { value: 'amber', label: 'Žuta (upozorenje)' },
          { value: 'blue', label: 'Plava (info)' },
        ] },
    ],
  },
  'breaking-expiry': {
    title: 'Istek breaking vijesti',
    description: 'Pravila automatskog istjecanja breaking najava — npr. poslije X sati ili na određeni datum.',
    icon: AlertTriangle,
    newLabel: 'Novo pravilo',
    fields: [
      { key: 'hours', label: 'Istek nakon (sati)', type: 'number', placeholder: '24' },
      { key: 'expire_at', label: 'Fiksni datum (ISO)', type: 'text', placeholder: '2026-12-31T23:59' },
    ],
  },
  'streams': {
    title: 'Upravljanje streamovima',
    description: 'Live video / audio streamovi koji se emitiraju na naslovnici ili u zasebnim rubrikama.',
    icon: Radio,
    newLabel: 'Novi stream',
    fields: [
      { key: 'stream_url', label: 'URL streama', type: 'url', placeholder: 'https://stream.example.com/live.m3u8' },
      { key: 'kind', label: 'Tip', type: 'select',
        options: [
          { value: 'video', label: 'Video (HLS/DASH)' },
          { value: 'audio', label: 'Audio (radio)' },
          { value: 'youtube', label: 'YouTube embed' },
        ] },
    ],
  },

  // ── Content ─────────────────────────────────────────────────────────────
  'imprint': {
    title: 'Impresum — Tim',
    description: 'Javni popis uredništva i kontakata koji se prikazuje na stranici /impressum.',
    icon: UsersRound,
    newLabel: 'Novi član tima',
    fields: [
      { key: 'role', label: 'Funkcija', type: 'text', placeholder: 'Glavni urednik' },
      { key: 'email', label: 'Email', type: 'text', placeholder: 'ime.prezime@e-mina.eu' },
      { key: 'photo_url', label: 'URL fotografije', type: 'url' },
    ],
  },
  'bulk-ops': {
    title: 'Skupne operacije',
    description: 'Predlošci za masovno objavljivanje, arhiviranje ili premještanje članaka u kategorije.',
    icon: ListOrdered,
    newLabel: 'Novi predložak',
    fields: [
      { key: 'operation', label: 'Operacija', type: 'select',
        options: [
          { value: 'publish', label: 'Objavi sve nacrte' },
          { value: 'archive', label: 'Arhiviraj starije od N dana' },
          { value: 'move_category', label: 'Premjesti u kategoriju' },
        ] },
      { key: 'params', label: 'Parametri (JSON)', type: 'textarea', placeholder: '{"days": 90}' },
    ],
  },

  // ── Portals ─────────────────────────────────────────────────────────────
  'times-posao': {
    title: 'Times Posao portal',
    description: 'Partnerski poslovni portal — plaćeni objavljivači i ključni klijenti.',
    icon: Briefcase,
    newLabel: 'Novi partner',
    fields: [
      { key: 'company', label: 'Tvrtka', type: 'text' },
      { key: 'contact', label: 'Kontakt osoba', type: 'text' },
      { key: 'contract_url', label: 'Ugovor (URL)', type: 'url' },
    ],
  },
  'sponsored-market': {
    title: 'Sponzorirano tržište',
    description: 'Cjenici i slotovi za sponzorirani sadržaj (nativna reklama).',
    icon: Store,
    newLabel: 'Novi slot',
    fields: [
      { key: 'slot', label: 'Pozicija', type: 'select',
        options: [
          { value: 'hero', label: 'Hero naslovna' },
          { value: 'sidebar', label: 'Bočna traka' },
          { value: 'in_article', label: 'U članku' },
          { value: 'newsletter', label: 'Newsletter' },
        ] },
      { key: 'price_eur', label: 'Cijena (EUR)', type: 'number' },
    ],
  },

  // ── Marketing ───────────────────────────────────────────────────────────
  'ads': {
    title: 'Upravljanje oglasima',
    description: 'Aktivne oglasne kampanje — banner, in-feed i native.',
    icon: Megaphone,
    newLabel: 'Nova kampanja',
    fields: [
      { key: 'client', label: 'Klijent', type: 'text' },
      { key: 'kind', label: 'Format', type: 'select',
        options: [
          { value: 'banner', label: 'Banner' },
          { value: 'native', label: 'Nativni oglas' },
          { value: 'takeover', label: 'Takeover' },
        ] },
      { key: 'target_url', label: 'Odredišni URL', type: 'url' },
      { key: 'start', label: 'Početak', type: 'text', placeholder: '2026-02-01' },
      { key: 'end', label: 'Kraj', type: 'text', placeholder: '2026-02-28' },
    ],
  },
  'advertise-analytics': {
    title: 'Analitika oglašavanja',
    description: 'Praćeni oglasni KPI — impresije, klikovi, CTR i prihod po kampanji.',
    icon: LineChart,
    newLabel: 'Novi izvještaj',
    fields: [
      { key: 'period', label: 'Razdoblje', type: 'text', placeholder: '2026-Q1' },
      { key: 'impressions', label: 'Impresije', type: 'number' },
      { key: 'clicks', label: 'Klikovi', type: 'number' },
      { key: 'revenue_eur', label: 'Prihod (EUR)', type: 'number' },
    ],
  },
  'social-schedule': {
    title: 'Raspored društvenih mreža',
    description: 'Plan objavljivanja na Facebook, X (Twitter), Instagram i LinkedIn.',
    icon: CalendarClock,
    newLabel: 'Nova objava',
    fields: [
      { key: 'network', label: 'Mreža', type: 'select',
        options: [
          { value: 'facebook', label: 'Facebook' },
          { value: 'twitter', label: 'X / Twitter' },
          { value: 'instagram', label: 'Instagram' },
          { value: 'linkedin', label: 'LinkedIn' },
        ] },
      { key: 'scheduled_at', label: 'Planirano vrijeme (ISO)', type: 'text' },
      { key: 'text', label: 'Tekst objave', type: 'textarea' },
    ],
  },

  // ── System ──────────────────────────────────────────────────────────────
  'chat-sessions': {
    title: 'Sesije čavrljanja',
    description: 'Povijesne sesije Emina AI asistenta — za reviziju i trening.',
    icon: MessageSquare,
    newLabel: 'Označi sesiju',
    fields: [
      { key: 'session_id', label: 'ID sesije', type: 'text' },
      { key: 'rating', label: 'Ocjena (1-5)', type: 'number' },
      { key: 'notes', label: 'Napomene urednika', type: 'textarea' },
    ],
  },
  'documentation': {
    title: 'Interna dokumentacija',
    description: 'Uredničke smjernice, stilski vodič i procedure — dostupne samo administratorima.',
    icon: Book,
    newLabel: 'Novi zapis',
    fields: [
      { key: 'category', label: 'Kategorija', type: 'select',
        options: [
          { value: 'editorial', label: 'Uredničko' },
          { value: 'tech', label: 'Tehničko' },
          { value: 'legal', label: 'Pravno' },
        ] },
      { key: 'body_md', label: 'Sadržaj (Markdown)', type: 'textarea' },
    ],
  },
  'content-reports': {
    title: 'Izvještaji o sadržaju',
    description: 'Ručni izvještaji o neprimjerenom sadržaju zaprimljeni izvan komentarskog sustava.',
    icon: AlertTriangle,
    newLabel: 'Novi izvještaj',
    fields: [
      { key: 'url', label: 'URL sadržaja', type: 'url' },
      { key: 'reason', label: 'Razlog', type: 'textarea' },
      { key: 'reporter', label: 'Email prijavitelja', type: 'text' },
    ],
  },
  'widgets': {
    title: 'Widgeti / Katalozi',
    description: 'Dodatni widgeti (npr. kino repertoar, blagdani, javni registri) — dostupni na stranici.',
    icon: LayoutList,
    newLabel: 'Novi widget',
    fields: [
      { key: 'kind', label: 'Tip widgeta', type: 'text', placeholder: 'weather, crypto, directory...' },
      { key: 'config_json', label: 'Konfiguracija (JSON)', type: 'textarea' },
    ],
  },
  'times-brands': {
    title: 'Times Brendovi',
    description: 'Lista brendiranih sub-portala (Times Posao, Times Zdravlje…).',
    icon: Megaphone,
    newLabel: 'Novi brend',
    fields: [
      { key: 'domain', label: 'Domena', type: 'text', placeholder: 'posao.e-mina.eu' },
      { key: 'logo_url', label: 'URL loga', type: 'url' },
    ],
  },
  'crypto-widget': {
    title: 'Kripto widget (postavke)',
    description: 'Definicija prikazanih kripto-valuta u javnom widgetu (CoinGecko ticker).',
    icon: Bitcoin,
    newLabel: 'Nova valuta',
    fields: [
      { key: 'coin_id', label: 'CoinGecko ID', type: 'text', placeholder: 'bitcoin, ethereum...' },
      { key: 'display_name', label: 'Naziv za prikaz', type: 'text' },
    ],
  },
  'portal-manage': {
    title: 'Upravljanje portalom',
    description: 'Operativne postavke portala — brendiranje, nazivi, kontaktne informacije.',
    icon: Eye,
    newLabel: 'Nova postavka',
    fields: [
      { key: 'setting_key', label: 'Ključ', type: 'text' },
      { key: 'value', label: 'Vrijednost', type: 'textarea' },
    ],
  },
  'doctor-revenue': {
    title: 'Zarada doktora',
    description: 'Partnerski Doctor portal — mjesečni prihod po doktoru i klinici.',
    icon: TrendingUp,
    newLabel: 'Novi unos',
    fields: [
      { key: 'doctor', label: 'Doktor', type: 'text' },
      { key: 'month', label: 'Mjesec', type: 'text', placeholder: '2026-02' },
      { key: 'amount_eur', label: 'Iznos (EUR)', type: 'number' },
    ],
  },
  'version-history': {
    title: 'Povijest verzija članaka',
    description: 'Označene važne verzije — npr. velike ispravke, sudski povezane izmjene.',
    icon: History,
    newLabel: 'Nova oznaka',
    fields: [
      { key: 'article_id', label: 'ID članka', type: 'text' },
      { key: 'reason', label: 'Razlog izmjene', type: 'textarea' },
    ],
  },
  'citizen-content': {
    title: 'Građanski sadržaj',
    description: 'Primljeni sadržaj od građana — pregled, moderacija i odobravanje.',
    icon: UserCheck,
    newLabel: 'Novi unos',
    fields: [
      { key: 'submitter_name', label: 'Ime podnositelja', type: 'text' },
      { key: 'submitter_email', label: 'Email', type: 'text' },
      { key: 'body', label: 'Sadržaj', type: 'textarea' },
    ],
  },
  'database': {
    title: 'Baza podataka',
    description: 'Ručno označene tablice / zbirke — npr. za backup strategije i migracije.',
    icon: Database,
    newLabel: 'Nova zbirka',
    fields: [
      { key: 'collection', label: 'Mongo collection', type: 'text' },
      { key: 'backup_policy', label: 'Politika sigurnosnog kopiranja', type: 'textarea' },
    ],
  },
  'offer-generator': {
    title: 'Generator ponuda',
    description: 'Predlošci komercijalnih ponuda za oglašivače i partnere.',
    icon: FileCheck,
    newLabel: 'Nova ponuda',
    fields: [
      { key: 'client', label: 'Klijent', type: 'text' },
      { key: 'total_eur', label: 'Iznos (EUR)', type: 'number' },
      { key: 'items_json', label: 'Stavke (JSON)', type: 'textarea' },
    ],
  },
  'footer-partners': {
    title: 'Footer partneri',
    description: 'Logo linkovi prikazani u donjem dijelu stranice (Liber Nostra, Grad, Županija…).',
    icon: Link2,
    newLabel: 'Novi partner',
    fields: [
      { key: 'link_url', label: 'Odredišni URL', type: 'url' },
      { key: 'logo_url', label: 'URL loga', type: 'url' },
    ],
  },
  'polls-analytics': {
    title: 'Analitika anketa',
    description: 'Dugoročni trendovi odziva anketa — usporedbe po mjesecima i temama.',
    icon: BarChart,
    newLabel: 'Novi zapis',
    fields: [
      { key: 'poll_id', label: 'ID ankete', type: 'text' },
      { key: 'votes', label: 'Broj glasova', type: 'number' },
      { key: 'winner', label: 'Pobjednički odgovor', type: 'text' },
    ],
  },

  // ── Fallback for any newly added sidebar item ───────────────────────────
  'generic': {
    title: 'Modul',
    description: 'Generički CRUD modul. Dodajte vlastite unose i upravljajte statusom.',
    icon: FileTextIcon,
    newLabel: 'Novi unos',
    fields: [],
  },
};

// Extras just to satisfy the tree-shaker — harmless unused imports.
export const _MODULE_ICONS_TOUCH = { UsersIcon, Map };
